import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {memo, useCallback, useState, useMemo} from 'react'

import {getProjectsReady} from '../../../selectors'
import {restApi} from '../../../services/rest'
import {stringifyId} from '../../../types'
import type {BuildId} from '../../../types'
import {noop} from '../../../utils/empty'
import {notNull} from '../../../utils/guards'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'

import ArtifactChangeItem from './ArtifactChange/ArtifactChange'
import ArtifactChangeDetailsPopup from './ArtifactChangeDetailsPopup/ArtifactChangeDetailsPopup'
import {getBuildArtifactDependenciesChangesArg} from './ArtifactChanges.rest'

import styles from './ArtifactChanges.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  buildId: BuildId
  visibleCount?: number
  withAdvancedRow?: boolean
  withExpandableLines?: boolean
  withSmallPadding?: boolean
  onShowChangeDetails?: () => unknown
  onHideChangeDetails?: () => unknown
}

function ArtifactChanges({
  className,
  buildId,
  visibleCount,
  withSmallPadding,
  withAdvancedRow,
  withExpandableLines,
  onHideChangeDetails = noop,
  onShowChangeDetails = noop,
}: Props) {
  const locator = `id:${stringifyId(buildId)}`
  const isProjectsReady = useAppSelector(getProjectsReady)

  const {changes, isChangesReady} = restApi.endpoints.getBuildNormalized.useQuery(
    getBuildArtifactDependenciesChangesArg(locator),
    {
      selectFromResult: ({data}) => ({
        changes: data?.entities.builds?.[data.result]?.artifactDependencyChanges?.buildChange,
        isChangesReady: data != null,
      }),
    },
  )
  const isReady = isProjectsReady && isChangesReady
  const buildIds = useMemo(
    () => changes?.map(change => change.nextBuild).filter(notNull) ?? [],
    [changes],
  )

  useSequenceLoaderReadyHandler(isReady)

  const [expandedChange, setExpandedChange] = useState<BuildId | null>(null)
  const toggleExpandedChange = useCallback(
    (id: BuildId) => {
      if (expandedChange === id) {
        setExpandedChange(null)
        onHideChangeDetails()
      } else {
        setExpandedChange(id)
        onShowChangeDetails()
      }
    },
    [expandedChange, setExpandedChange, onShowChangeDetails, onHideChangeDetails],
  )
  const visibleBuildIds = visibleCount != null ? buildIds.slice(0, visibleCount) : buildIds

  if (!isReady) {
    return (
      <div className={styles.changesListEmpty}>
        <LoaderInline>{'Loading...'}</LoaderInline>
      </div>
    )
  }

  return buildIds.length > 0 ? (
    <div className={className}>
      {visibleBuildIds.map(id => (
        <ArtifactChangeItem
          key={stringifyId(id)}
          from={id}
          to={buildId}
          expanded={withExpandableLines === true && expandedChange === id}
          toggleExpandedChange={toggleExpandedChange}
          withSmallPadding={withSmallPadding}
          withAdvancedRow={withAdvancedRow}
          withExpandableLines={withExpandableLines}
        />
      ))}
      {expandedChange != null && (
        <ArtifactChangeDetailsPopup
          targetBuildId={expandedChange}
          buildIds={buildIds}
          show={withExpandableLines !== true && true}
          cancelDialog={toggleExpandedChange}
        />
      )}
    </div>
  ) : (
    <div className={styles.changesListEmpty}>{'Changes list is empty'}</div>
  )
}

export default memo<Props>(ArtifactChanges)
